import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Badge, Alert, ProgressBar, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { DOCUMENT_CATEGORIES, LEARNER_DOCUMENT_CATEGORY_ORDER, checkDocumentDeadline } from '../config/documentCategories';
import { DOCUMENT_CATEGORY_CONFIG, DOCUMENT_CATEGORY_CONFIG_BY_ID } from '../constants/documentCategories';
import documentDeadlineService from '../services/documentDeadlineService';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

const DocumentStatusSummary = ({
  documentData = null,
  registrationDate: registrationDateProp = null,
  loading: loadingProp = false
}) => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(!documentData && !loadingProp);
  const [error, setError] = useState(null);
  const [state, setState] = useState(documentData);
  const [programRequiredDocs, setProgramRequiredDocs] = useState(null);

  const registrationDate = useMemo(() => {
    if (registrationDateProp) {
      return registrationDateProp;
    }
    if (userProfile?.createdAt?.toDate) {
      return userProfile.createdAt.toDate();
    }
    if (userProfile?.createdAt) {
      return new Date(userProfile.createdAt);
    }
    return new Date();
  }, [registrationDateProp, userProfile]);

  // Fetch program's required documents
  useEffect(() => {
    const loadProgramRequirements = async () => {
      const resolveRequiredCategoryKey = (rawValue) => {
        if (!rawValue || typeof rawValue !== 'string') {
          return null;
        }

        if (DOCUMENT_CATEGORIES[rawValue]) {
          return rawValue;
        }

        if (DOCUMENT_CATEGORY_CONFIG[rawValue]) {
          return DOCUMENT_CATEGORY_CONFIG[rawValue].key;
        }

        if (DOCUMENT_CATEGORY_CONFIG_BY_ID[rawValue]) {
          return DOCUMENT_CATEGORY_CONFIG_BY_ID[rawValue].key;
        }

        const normalized = String(rawValue).toUpperCase();
        if (DOCUMENT_CATEGORIES[normalized]) {
          return normalized;
        }

        return null;
      };

      if (!userProfile?.programId) {
        // If no program assigned, use learner-specific documents (Phase 1 - only 3 documents)
        setProgramRequiredDocs(LEARNER_DOCUMENT_CATEGORY_ORDER);
        return;
      }

      try {
        const programDoc = await getDoc(doc(db, 'programs', userProfile.programId));
        if (programDoc.exists()) {
          const programData = programDoc.data();
          // Use program's requiredDocuments if set, otherwise fall back to learner documents
          if (programData.requiredDocuments && programData.requiredDocuments.length > 0) {
            const normalizedRequiredDocs = Array.from(
              new Set(programData.requiredDocuments.map(resolveRequiredCategoryKey).filter(Boolean))
            );
            setProgramRequiredDocs(
              normalizedRequiredDocs.length > 0 ? normalizedRequiredDocs : LEARNER_DOCUMENT_CATEGORY_ORDER
            );
          } else {
            // Use learner-specific documents (Phase 1 - only 3 documents)
            setProgramRequiredDocs(LEARNER_DOCUMENT_CATEGORY_ORDER);
          }
        } else {
          // Program not found, use learner-specific documents
          setProgramRequiredDocs(LEARNER_DOCUMENT_CATEGORY_ORDER);
        }
      } catch (err) {
        devError('Error loading program requirements:', err);
        // On error, fall back to learner-specific documents
        setProgramRequiredDocs(LEARNER_DOCUMENT_CATEGORY_ORDER);
      }
    };

    loadProgramRequirements();
  }, [userProfile?.programId]);

  useEffect(() => {
    let isMounted = true;

    const loadDocuments = async () => {
      if (!currentUser || documentData) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await documentDeadlineService.getUserDocumentStatus(currentUser.uid);
        if (!isMounted) return;
        if (result.success) {
          setState(result.data);
        } else {
          setError(result.error || 'Failed to load document status.');
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load document status.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDocuments();

    // Listen for document upload events to refresh status
    const handleDocumentUploaded = () => {
      devLog('Document uploaded event received, refreshing status...');
      loadDocuments();
    };

    window.addEventListener('documentUploaded', handleDocumentUploaded);

    return () => {
      isMounted = false;
      window.removeEventListener('documentUploaded', handleDocumentUploaded);
    };
  }, [currentUser, documentData]);

  const resolvedData = state || documentData;
  const documentStatusMap = resolvedData?.documentStatus || {};
  const deadlineInfo = resolvedData?.deadlineInfo || checkDocumentDeadline(registrationDate);

  // Use program-specific required documents if available, otherwise fall back to learner documents
  const requiredDocumentCategories = programRequiredDocs || LEARNER_DOCUMENT_CATEGORY_ORDER;

  // For learners, only show the required categories (no optional documents shown)
  const allCategories = [...requiredDocumentCategories];

  const documentStats = allCategories.map((categoryKey) => {
    const categoryInfo = DOCUMENT_CATEGORIES[categoryKey];
    const statusEntry = documentStatusMap[categoryKey] || documentStatusMap[categoryInfo?.id];
    const status = statusEntry?.status || 'missing';
    const document = statusEntry?.document || null;

    return {
      category: categoryInfo?.name || categoryKey,
      categoryKey,
      status,
      document,
      isOptional: !categoryInfo?.mandatory,
      description: categoryInfo?.description,
      helpText: categoryInfo?.helpText,
      feedback:
        document?.rejectionReason ||
        document?.reviewNotes ||
        statusEntry?.document?.rejectionReason ||
        statusEntry?.document?.reviewNotes ||
        null
    };
  });

  const requiredDocs = documentStats.filter((doc) => !doc.isOptional);
  const verifiedCount = requiredDocs.filter((doc) => doc.status === 'verified').length;
  const pendingCount = requiredDocs.filter((doc) => doc.status === 'pending').length;
  const rejectedCount = requiredDocs.filter((doc) => doc.status === 'rejected').length;
  const missingCount = requiredDocs.filter((doc) => doc.status === 'missing').length;
  const completionPercentage = requiredDocs.length > 0
    ? Math.round((verifiedCount / requiredDocs.length) * 100)
    : 0;

  // Outstanding docs are only missing or rejected (not pending - those are already uploaded)
  const outstandingDocs = documentStats.filter(
    (doc) => !doc.isOptional && ['missing', 'rejected'].includes(doc.status)
  );

  const hasOverdueDocuments = outstandingDocs.length > 0 && deadlineInfo.isOverdue;
  const hasUrgentDocuments = outstandingDocs.length > 0 && deadlineInfo.isUrgent;

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'danger';
      case 'missing':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
      case 'approved':
        return '✅';
      case 'pending':
        return '⏳';
      case 'rejected':
        return '❌';
      case 'missing':
        return '📄';
      default:
        return '📄';
    }
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Document Completion Status</h5>
        </Card.Header>
        <Card.Body className="text-center text-muted">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading document status...
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Document Status Unavailable</Alert.Heading>
        <p className="mb-0">{error}</p>
      </Alert>
    );
  }

  return (
    <div className="document-status-summary">
      {hasOverdueDocuments && (
        <Alert variant="danger" className="mb-4">
          <Alert.Heading>🚨 OVERDUE: Document Submission Required</Alert.Heading>
          <p className="mb-2">
            <strong>Your document submission deadline was {deadlineInfo.deadlineDate.toLocaleDateString()}.</strong>
            <br />
            You are {Math.abs(deadlineInfo.daysUntilDeadline)} days overdue. Please submit the following documents immediately:
          </p>
          <ul className="mb-0">
            {outstandingDocs.map((doc) => (
              <li key={doc.categoryKey}>
                <strong>{DOCUMENT_CATEGORIES[doc.categoryKey]?.name || doc.category}</strong>
                : {doc.status === 'missing' ? 'Not uploaded' : doc.status}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {hasUrgentDocuments && !hasOverdueDocuments && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>⚠️ URGENT: Document Submission Due Soon</Alert.Heading>
          <p className="mb-2">
            <strong>Deadline: {deadlineInfo.deadlineDate.toLocaleDateString()} ({deadlineInfo.daysUntilDeadline} days remaining)</strong>
            <br />
            Please submit the following documents as soon as possible:
          </p>
          <ul className="mb-0">
            {outstandingDocs.map((doc) => (
              <li key={doc.categoryKey}>
                <strong>{DOCUMENT_CATEGORIES[doc.categoryKey]?.name || doc.category}</strong>
                : {doc.status === 'missing' ? 'Not uploaded' : doc.status}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {outstandingDocs.length > 0 && !hasOverdueDocuments && !hasUrgentDocuments && (
        <Alert variant="info" className="mb-4">
          <Alert.Heading>📊 Outstanding Documents ({outstandingDocs.length})</Alert.Heading>
          <p className="mb-2">
            <strong>Submission Deadline: {deadlineInfo.deadlineDate.toLocaleDateString()}</strong>
            <br />
            You have documents that require attention:
          </p>
          <ul className="mb-0">
            {outstandingDocs.map((doc) => (
              <li key={doc.categoryKey}>
                <strong>{DOCUMENT_CATEGORIES[doc.categoryKey]?.name || doc.category}</strong>
                : {doc.status === 'missing' ? 'Not uploaded' : doc.status}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col lg={3} md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center">
              <h2 className="text-success mb-1">{verifiedCount}</h2>
              <small className="text-muted">Verified</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center">
              <h2 className="text-warning mb-1">{pendingCount}</h2>
              <small className="text-muted">Pending Review</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center">
              <h2 className="text-danger mb-1">{rejectedCount}</h2>
              <small className="text-muted">Rejected</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={3} md={6}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body className="text-center">
              <h2 className="text-secondary mb-1">{missingCount}</h2>
              <small className="text-muted">Missing</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Document Checklist</h6>
            <Badge bg="primary">{completionPercentage}% Complete</Badge>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            {documentStats.map((doc) => (
              <Col lg={3} md={4} sm={6} key={doc.categoryKey} className="mb-3">
                <div className="border rounded p-2 h-100">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <small className="fw-bold">{doc.category}</small>
                    <Badge bg={doc.isOptional ? 'info' : 'secondary'} className="ms-1" style={{fontSize: '0.65rem'}}>
                      {doc.isOptional ? 'Optional' : 'Required'}
                    </Badge>
                  </div>
                <div className="d-flex align-items-center mt-2">
                  <span className={`text-${getStatusColor(doc.status)} small`}>
                    {getStatusIcon(doc.status)} {doc.status}
                  </span>
                </div>
                  {doc.status === 'rejected' && doc.feedback ? (
                    <div className="mt-2 small text-danger">
                      <strong>Feedback:</strong> {doc.feedback}
                    </div>
                  ) : null}
                </div>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DocumentStatusSummary;
